import React, { useState, useEffect } from 'react';
import { getDataPackages, normalizePackageDescription, DataPackage } from '../../services/dataPackagesService';
import AccessDeniedModal from './AccessDeniedModal';

interface DataPackagesModalProps {
  onClose: () => void;
  onPackageSelect?: (packageName: string, packageDisplayName: string) => void;
}

const DataPackagesModal: React.FC<DataPackagesModalProps> = ({ onClose, onPackageSelect }) => {
  const [dataPackages, setDataPackages] = useState<DataPackage[]>([]);
  const [packageAccess, setPackageAccess] = useState<{[key: string]: boolean}>({});
  const [loading, setLoading] = useState(true);
  const [accessDeniedModal, setAccessDeniedModal] = useState<{isOpen: boolean, packageName: string}>({
    isOpen: false,
    packageName: ''
  });

  const handlePackageOpen = (packageName: string | null, packageDisplayName: string, hasAccess: boolean) => {
    if (!packageName) {
      alert('Данные пакета не готовы');
      return;
    }
    
    if (!hasAccess) {
      setAccessDeniedModal({
        isOpen: true,
        packageName: packageDisplayName
      });
      return;
    }
    
    if (onPackageSelect) {
      onPackageSelect(packageName, packageDisplayName);
      onClose();
    }
  };

  const handleCloseAccessDeniedModal = () => {
    setAccessDeniedModal({
      isOpen: false,
      packageName: ''
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const packages = await getDataPackages();
        setDataPackages(packages);

        const accessChecks: {[key: string]: boolean} = {};
        for (const pkg of packages) {
          if (pkg.package_name) {
            accessChecks[pkg.package_name] = pkg.hasAccess === true;
          }
        }
        setPackageAccess(accessChecks);
      } catch (error) {
        console.error('Error fetching data packages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content-loading" onClick={(e) => e.stopPropagation()}>
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
            <h2 className="loading-title">Загрузка пакетов данных</h2>
            <p className="loading-subtitle">Получаем актуальную информацию...</p>
            <div className="loading-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
            </div>
          </div>
          <button className="modal-close-loading" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content-new" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-new">
          <h2 className="modal-title-new">Пакетов данных: {dataPackages.length}</h2>
          <button className="modal-close-new" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="modal-body-new">
          {dataPackages.map((pkg, index) => (
            <div key={pkg.id} className="package-item-new">
              <div className="package-content-new">
                <div className="package-col-1">
                  <h3 className="package-name-new">{pkg.name}</h3>
                </div>
                
                <div className="package-col-2">
                  <p className="package-description-new">{normalizePackageDescription(pkg.description)}</p>
                </div>
                
                <div className="package-col-3">
                  <div className="meta-item-new">
                    <i className="fas fa-building"></i>
                    <span>{pkg.companies_data.length} компаний</span>
                  </div>
                  
                  <div className="meta-item-new">
                    <i className="fas fa-industry"></i>
                    <span>{pkg.companies_data.map(company => company.sector).reduce((unique: string[], sector: string) => unique.includes(sector) ? unique : [...unique, sector], []).join(', ')}</span>
                  </div>
                  
                  <div className="meta-item-new">
                    <i className="fas fa-money-bill-wave"></i>
                    <span>Постоянная компенсация, Переменная компенсация, Долгосрочная мотивация</span>
                  </div>
                  
                  <div className="meta-item-new">
                    <i className="fas fa-user-tie"></i>
                    <span>{pkg.positions.join(', ')}</span>
                  </div>
                </div>
                
                <div className="package-col-4">
                  {(() => {
                    const hasAccess = pkg.package_name ? packageAccess[pkg.package_name] : false;
                    const isReady = !!pkg.package_name;
                    const canAccess = isReady && hasAccess;
                    
                    let buttonText = 'Открыть пакет';
                    let buttonStyle = {
                      background: 'linear-gradient(135deg, #4caf50, #45a049)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: 1
                    };
                    
                    if (!isReady) {
                      buttonText = 'Данные не готовы';
                      buttonStyle.background = 'linear-gradient(135deg, #f44336, #d32f2f)';
                    } else if (!hasAccess) {
                      buttonText = 'Доступ ограничен';
                      buttonStyle.background = 'linear-gradient(135deg, #f44336, #d32f2f)';
                    }
                    
                    return (
                      <button 
                        className="package-open-button"
                        onClick={() => handlePackageOpen(pkg.package_name, pkg.name, hasAccess)}
                        style={buttonStyle}
                        onMouseEnter={(e) => {
                          if (canAccess) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #45a049, #3d8b40)';
                          } else {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #d32f2f, #b71c1c)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (canAccess) {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #4caf50, #45a049)';
                          } else {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #f44336, #d32f2f)';
                          }
                        }}
                      >
                        <span>{buttonText}</span>
                      </button>
                    );
                  })()}
                </div>
              </div>
              {index < dataPackages.length - 1 && <div className="package-divider-new"></div>}
            </div>
          ))}
        </div>
      </div>

      {/* Модальное окно "Доступ ограничен" */}
      <AccessDeniedModal
        isOpen={accessDeniedModal.isOpen}
        onClose={handleCloseAccessDeniedModal}
        packageName={accessDeniedModal.packageName}
      />
    </div>
  );
};

export default DataPackagesModal;
